import {
    DataTexture,
    Mesh,
    NearestFilter,
    NoColorSpace,
    OrthographicCamera,
    PlaneGeometry,
    RedFormat,
    RGBAFormat,
    Scene,
    ShaderMaterial,
    UnsignedByteType,
    WebGLRenderTarget,
} from 'three';
import type { Texture, WebGLRenderer } from 'three';
import {
    SHADOW_ALPHA_TEST,
    SHADOW_RESOLUTION,
} from './config';

export interface ShadowCaptureResources {
    camera: OrthographicCamera
    geometry: PlaneGeometry
    material: ShaderMaterial
    pixels: Uint8Array
    scene: Scene
    target: WebGLRenderTarget
}

/**
 * Allocate the reusable GPU readback pass once per shadow hook. The pass
 * normalizes Drei's RGB accumulation target before pixels are compacted to R8.
 */
export function createShadowCaptureResources():ShadowCaptureResources {
    const camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geometry = new PlaneGeometry(2, 2);
    const material = new ShaderMaterial({
        depthTest: false,
        depthWrite: false,
        fragmentShader: `
            uniform sampler2D sourceMap;
            uniform float alphaTest;
            varying vec2 vUv;

            void main() {
                vec3 source = texture2D(sourceMap, vUv).rgb;
                float encodedShadow = clamp(
                    (source.r + source.g + source.b) / alphaTest,
                    0.0,
                    1.0
                );
                gl_FragColor = vec4(vec3(encodedShadow), 1.0);
            }
        `,
        toneMapped: false,
        uniforms: {
            alphaTest: {value: SHADOW_ALPHA_TEST},
            sourceMap: {value: null},
        },
        vertexShader: `
            varying vec2 vUv;

            void main() {
                vUv = uv;
                gl_Position = vec4(position.xy, 0.0, 1.0);
            }
        `,
    });
    const scene = new Scene();
    scene.add(new Mesh(geometry, material));

    const target = new WebGLRenderTarget(SHADOW_RESOLUTION, SHADOW_RESOLUTION, {
        depthBuffer: false,
        format: RGBAFormat,
        magFilter: NearestFilter,
        minFilter: NearestFilter,
        stencilBuffer: false,
        type: UnsignedByteType,
    });
    target.texture.colorSpace = NoColorSpace;
    target.texture.generateMipmaps = false;

    return {
        camera,
        geometry,
        material,
        pixels: new Uint8Array(SHADOW_RESOLUTION * SHADOW_RESOLUTION * 4),
        scene,
        target,
    };
}

/**
 * Drei keeps the accumulated result in a renderer-owned RGB target. Copy it
 * through a tiny normalization shader, read it once, and retain only one byte
 * per pixel in a CPU-backed texture. That makes cached shadows independent of
 * Drei's reusable ping-pong targets and substantially reduces cache memory.
 */
export function captureShadowTexture(
    renderer:WebGLRenderer,
    sourceMap:Texture,
    resources:ShadowCaptureResources,
    cacheKey:string,
):DataTexture {
    const previousRenderTarget = renderer.getRenderTarget();
    const previousAutoClear = renderer.autoClear;
    const previousScissorTest = renderer.getScissorTest();
    const previousXrEnabled = renderer.xr.enabled;

    resources.material.uniforms.sourceMap.value = sourceMap;

    try {
        renderer.autoClear = true;
        renderer.xr.enabled = false;
        renderer.setScissorTest(false);
        renderer.setRenderTarget(resources.target);
        renderer.clear();
        renderer.render(resources.scene, resources.camera);
        renderer.readRenderTargetPixels(
            resources.target,
            0,
            0,
            SHADOW_RESOLUTION,
            SHADOW_RESOLUTION,
            resources.pixels,
        );
    } finally {
        renderer.setRenderTarget(previousRenderTarget);
        renderer.setScissorTest(previousScissorTest);
        renderer.xr.enabled = previousXrEnabled;
        renderer.autoClear = previousAutoClear;
    }

    // Every channel contains the normalized grayscale result. Retaining one
    // byte per texel cuts the page-lifetime cache to one quarter of RGBA size.
    const redPixels = new Uint8Array(SHADOW_RESOLUTION * SHADOW_RESOLUTION);
    for(let sourceIndex = 0, targetIndex = 0; sourceIndex < resources.pixels.length; sourceIndex += 4) {
        redPixels[targetIndex] = resources.pixels[sourceIndex];
        targetIndex++;
    }

    const texture = new DataTexture(
        redPixels,
        SHADOW_RESOLUTION,
        SHADOW_RESOLUTION,
        RedFormat,
        UnsignedByteType,
    );
    texture.colorSpace = NoColorSpace;
    texture.flipY = false;
    texture.generateMipmaps = false;
    texture.magFilter = NearestFilter;
    texture.minFilter = NearestFilter;
    texture.name = `ModelViewer cached shadow: ${cacheKey}`;
    texture.unpackAlignment = 1;
    texture.needsUpdate = true;

    return texture;
}

export function disposeShadowCaptureResources(resources:ShadowCaptureResources):void {
    resources.geometry.dispose();
    resources.material.dispose();
    resources.target.dispose();
}
